import React, { useState, useRef } from 'react';
import { View, Pressable, TextInput } from 'react-native-web';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import CustomText from '../../../../components/atoms/CustomText';
import { colors, spacing } from '../../../../utils/styling';
import BaseModal from '../../../../components/popups/BaseModal';
import { typography } from '../../../../utils/styling';
import { useParams } from 'react-router-dom';
import { useAccountContacts } from '../../../../lib/salesforce';
import { useInviteUser } from '../../../../lib/auth';
import { 
  useOnboardingTasks, 
  useCreateOnboardingTask, 
  useUpdateOnboardingTask, 
  useDeleteOnboardingTask,
  OnboardingTaskCreateInput,
  OnboardingTaskUpdateInput,
  useTasksBySfAccount,
} from '../../../../lib/onboarding_tasks';
import type { OnboardingTask } from '../../../../lib/onboarding_tasks';

interface LocalTaskState {
  id: number;
  order: number;
  status: 'pending' | 'completed';
}

// SVG for the drag handle
const DragHandleIcon = () => (
  <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="2" cy="2" r="2" fill={colors.gray[400]} />
    <circle cx="2" cy="10" r="2" fill={colors.gray[400]} />
    <circle cx="2" cy="18" r="2" fill={colors.gray[400]} />
    <circle cx="10" cy="2" r="2" fill={colors.gray[400]} />
    <circle cx="10" cy="10" r="2" fill={colors.gray[400]} />
    <circle cx="10" cy="18" r="2" fill={colors.gray[400]} />
  </svg>
);

interface OnboardingTabProps {
  isClientPortal?: boolean;
  clientId?: string;
}

export default function OnboardingTab({ isClientPortal = false, clientId: propClientId }: OnboardingTabProps) {
  const { clientId: urlClientId } = useParams();
  const effectiveClientId = propClientId || urlClientId;
  const [showCompletedTasks, setShowCompletedTasks] = useState(true);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingTask, setEditingTask] = useState<OnboardingTask | null>(null);
  const [taskForm, setTaskForm] = useState<OnboardingTaskCreateInput>({
    title: '',
    description: '',
    due: '',
    sf_account: effectiveClientId || ''
  });

  // Local state for task order and status
  const [localTaskStates, setLocalTaskStates] = useState<Record<number, LocalTaskState>>({});

  // Fetch tasks for this client - always filter by sf_account
  const { data: tasks = [], isLoading } = useTasksBySfAccount(effectiveClientId || '');
  
  // Initialize local state for new tasks
  React.useEffect(() => {
    const newLocalStates: Record<number, LocalTaskState> = {};
    tasks.forEach((task, index) => {
      if (!localTaskStates[task.id]) {
        newLocalStates[task.id] = {
          id: task.id,
          order: index,
          status: 'pending'
        };
      }
    });
    if (Object.keys(newLocalStates).length > 0) {
      setLocalTaskStates(prev => ({...prev, ...newLocalStates}));
    }
  }, [tasks]);

  // Get sorted tasks with local state
  const sortedTasks = [...tasks].sort((a, b) => 
    (localTaskStates[a.id]?.order ?? 0) - (localTaskStates[b.id]?.order ?? 0)
  );
  
  // Mutations
  const createTask = useCreateOnboardingTask();
  const updateTask = useUpdateOnboardingTask();
  const deleteTask = useDeleteOnboardingTask();

  // Get client's contact info
  const { data: contactsData } = useAccountContacts(effectiveClientId || '');
  const primaryContact = contactsData?.records?.[0];
  const defaultEmail = primaryContact?.Email || '';

  // Get invite mutation
  const { mutateAsync: inviteUser } = useInviteUser();

  const handleOpenAddModal = () => {
    setTaskForm({ 
      title: '', 
      description: '', 
      due: '', 
      sf_account: effectiveClientId || '' 
    });
    setShowAddTaskModal(true);
  };

  const handleOpenEditModal = (task: OnboardingTask) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      due: task.due || '',
      sf_account: task.sf_account || ''
    });
    setShowEditTaskModal(true);
  };

  const handleAddTask = async (values: OnboardingTaskCreateInput) => {
    try {
      const taskData = {
        ...values,
        sf_account: effectiveClientId || '',
        order: tasks.length,
        status: 'pending'
      };
      await createTask.mutateAsync(taskData);
      setShowAddTaskModal(false);
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const handleUpdateTask = async (values: OnboardingTaskUpdateInput) => {
    if (!editingTask) return;

    try {
      await updateTask.mutateAsync({
        taskId: editingTask.id,
        taskData: values
      });
      setShowEditTaskModal(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleToggleStatus = (task: OnboardingTask) => {
    setLocalTaskStates(prev => ({
      ...prev,
      [task.id]: {
        ...prev[task.id],
        status: prev[task.id]?.status === 'completed' ? 'pending' : 'completed'
      }
    }));
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      await deleteTask.mutateAsync(taskId);
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleInviteUser = async (email: string) => {
    try {
      await inviteUser({
        email,
        profile: {
          first_name: primaryContact?.FirstName || '',
          last_name: primaryContact?.LastName || '',
          user_type: 'client',
          sf_account: effectiveClientId || '',
          email: email
        }
      });
      setShowInviteModal(false);
    } catch (err) {
      console.error('Failed to invite user:', err);
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(sortedTasks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local order state
    const newLocalStates = {...localTaskStates};
    items.forEach((task, index) => {
      newLocalStates[task.id] = {
        ...newLocalStates[task.id],
        order: index
      };
    });
    setLocalTaskStates(newLocalStates);
  };

  const TaskModal = ({ 
    isVisible, 
    onClose, 
    title, 
    onSubmit, 
    submitText,
    initialValues = { title: '', description: '', due: '' }
  }: { 
    isVisible: boolean; 
    onClose: () => void; 
    title: string; 
    onSubmit: (values: OnboardingTaskCreateInput) => void; 
    submitText: string;
    initialValues?: Partial<OnboardingTaskCreateInput>;
  }) => {
    const titleRef = useRef<HTMLInputElement>(null);
    const descriptionRef = useRef<HTMLInputElement>(null);
    const dueDateRef = useRef<HTMLInputElement>(null);

    const handleSubmit = () => {
      const title = titleRef.current?.value || '';
      const description = descriptionRef.current?.value || '';
      const dueDate = dueDateRef.current?.value;

      // Validate title is not empty
      if (!title.trim()) {
        alert('Please enter a task title');
        return;
      }
      
      // Only include due date if it's not empty
      const values = {
        title: title.trim(),
        description: description.trim(),
        ...(dueDate ? { due: dueDate } : {}), // dueDate is already in YYYY-MM-DD format from the input
        sf_account: effectiveClientId || ''
      };
      
      console.log('📝 TASK MODAL: Submitting values:', values);
      console.log('📝 TASK MODAL: Title value:', title);
      console.log('📝 TASK MODAL: Description value:', description);
      console.log('📝 TASK MODAL: Due date value:', dueDate);
      onSubmit(values);
    };

    return (
      <BaseModal
        isVisible={isVisible}
        onClose={onClose}
        title={title}
        width={600}
      >
        <View style={styles.modalContent}>
          <TextInput
            ref={titleRef}
            defaultValue={initialValues.title}
            placeholder="Task title"
            style={styles.input}
          />
          <TextInput
            ref={descriptionRef}
            defaultValue={initialValues.description}
            placeholder="Task description"
            multiline
            style={[styles.input, styles.textArea]}
          />
          <input
            ref={dueDateRef}
            defaultValue={initialValues.due}
            placeholder="Due date"
            type="date"
            style={styles.input as any}
          />
          <View style={styles.modalButtons}>
            <Pressable 
              style={[styles.button, styles.cancelButton]}
              onPress={onClose}
            >
              <CustomText style={styles.cancelButtonText}>Cancel</CustomText>
            </Pressable>
            <Pressable 
              style={[styles.button, styles.submitButton]}
              onPress={handleSubmit}
            >
              <CustomText style={styles.submitButtonText}>{submitText}</CustomText>
            </Pressable>
          </View>
        </View>
      </BaseModal>
    );
  };

  const InviteModal = ({ 
    isVisible, 
    onClose,
    defaultEmail,
    onSubmit 
  }: { 
    isVisible: boolean; 
    onClose: () => void;
    defaultEmail: string;
    onSubmit: (email: string) => void;
  }) => {
    const emailRef = useRef<HTMLInputElement>(null);

    const handleSubmit = () => {
      const email = emailRef.current?.value || '';
      if (email) {
        onSubmit(email);
      }
    };

    return (
      <BaseModal
        isVisible={isVisible}
        onClose={onClose}
        title="Invite Client"
        width={400}
      >
        <View>
          <CustomText style={styles.modalLabel}>Email Address</CustomText>
          <TextInput
            ref={emailRef}
            defaultValue={defaultEmail}
            placeholder="client@example.com"
            style={styles.input}
            type="email"
          />
          <View style={styles.modalButtons}>
            <Pressable 
              style={[styles.button, styles.cancelButton]}
              onPress={onClose}
            >
              <CustomText style={styles.cancelButtonText}>Cancel</CustomText>
            </Pressable>
            <Pressable 
              style={[styles.button, styles.submitButton]}
              onPress={handleSubmit}
            >
              <CustomText style={styles.submitButtonText}>Send Invite</CustomText>
            </Pressable>
          </View>
        </View>
      </BaseModal>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.content}>
        <CustomText>Loading tasks...</CustomText>
      </View>
    );
  }

  return (
    <View style={styles.content}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <CustomText style={styles.title}>
            Client Onboarding Tasks ({showCompletedTasks ? tasks.length : tasks.filter(t => localTaskStates[t.id]?.status !== 'completed').length})
          </CustomText>
          <Pressable 
            style={styles.filterButton}
            onPress={() => setShowCompletedTasks(!showCompletedTasks)}
          >
            <CustomText style={styles.filterButtonText}>
              {showCompletedTasks ? 'Hide Completed' : 'Show Completed'}
            </CustomText>
          </Pressable>
        </View>
        <View style={styles.headerRight}>
          {!isClientPortal && (
            <Pressable 
              style={styles.actionButton}
              onPress={() => setShowInviteModal(true)}
            >
              <CustomText style={styles.actionButtonText}>Invite User</CustomText>
            </Pressable>
          )}
          <Pressable 
            style={styles.addButton}
            onPress={handleOpenAddModal}
          >
            <CustomText style={styles.addButtonText}>+ Add Task</CustomText>
          </Pressable>
        </View>
      </View>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="tasks">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={styles.tasksList}
            >
              {sortedTasks
                .filter(task => showCompletedTasks || localTaskStates[task.id]?.status !== 'completed')
                .map((task, index) => (
                  <Draggable key={task.id} draggableId={task.id.toString()} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...styles.taskCard,
                          ...(localTaskStates[task.id]?.status === 'completed' && styles.completedTask),
                          ...(snapshot.isDragging && styles.draggingTask),
                          ...provided.draggableProps.style,
                        }}
                      >
                        <Pressable
                          style={styles.checkbox}
                          onPress={() => handleToggleStatus(task)}
                        >
                          {localTaskStates[task.id]?.status === 'completed' && (
                            <View style={styles.checkmark} />
                          )}
                        </Pressable>

                        <View style={styles.taskContent}>
                          <CustomText style={[
                            styles.taskTitle,
                            localTaskStates[task.id]?.status === 'completed' && styles.completedText
                          ]}>
                            {task.title}
                          </CustomText>
                          {task.description && (
                            <CustomText style={[
                              styles.taskDescription,
                              localTaskStates[task.id]?.status === 'completed' && styles.completedText
                            ]}>
                              {task.description}
                            </CustomText>
                          )}
                          {task.due && (
                            <CustomText style={styles.dueDate}>
                              Due: {new Date(task.due).toLocaleDateString()}
                            </CustomText>
                          )}
                        </View>

                        <View style={styles.taskActions}>
                          {localTaskStates[task.id]?.status === 'completed' ? (
                            <View style={styles.completedBadge}>
                              <CustomText style={styles.completedBadgeText}>
                                Completed
                              </CustomText>
                            </View>
                          ) : (
                            <>
                              <Pressable
                                style={styles.actionButton}
                                onPress={() => handleOpenEditModal(task)}
                              >
                                <CustomText style={styles.actionButtonText}>
                                  Edit
                                </CustomText>
                              </Pressable>
                              <Pressable
                                style={[styles.actionButton, styles.deleteButton]}
                                onPress={() => handleDeleteTask(task.id)}
                              >
                                <CustomText style={[styles.actionButtonText, styles.deleteButtonText]}>
                                  Delete
                                </CustomText>
                              </Pressable>
                            </>
                          )}
                        </View>

                        <div {...provided.dragHandleProps} style={styles.dragHandle}>
                          <DragHandleIcon />
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <TaskModal
        isVisible={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        title="Create New Task"
        onSubmit={handleAddTask}
        submitText="Create Task"
      />

      <TaskModal
        isVisible={showEditTaskModal}
        onClose={() => setShowEditTaskModal(false)}
        title="Edit Task"
        onSubmit={handleUpdateTask}
        submitText="Save Changes"
        initialValues={taskForm}
      />

      <InviteModal
        isVisible={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        defaultEmail={defaultEmail}
        onSubmit={handleInviteUser}
      />
    </View>
  );
}

const styles = {
  content: {
    padding: spacing.xl,
    maxWidth: 1200,
    marginHorizontal: 'auto',
    width: '100%',
    gap: spacing.xl,
    display: 'flex',
  } as React.CSSProperties,
  header: {
    flexDirection: 'row',
    flexWrap: 'wrap', // wraps on a phone instead of running off the screen (2026-10-07)
    rowGap: spacing.sm,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  } as React.CSSProperties,
  title: {
    fontSize: 24,
    fontWeight: '600',
    color: colors.dark,
  },
  addButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 8,
    cursor: 'pointer',
  } as React.CSSProperties,
  addButtonText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '600',
  },
  modalContent: {
    padding: spacing.xl,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  } as React.CSSProperties,
  modalButtons: {
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'flex-end',
    marginTop: spacing.lg,
  } as React.CSSProperties,
  input: {
    borderWidth: 1,
    borderColor: colors.gray[200],
    borderRadius: 4,
    padding: spacing.sm,
    fontSize: 14,
    color: colors.dark,
    backgroundColor: colors.white,
  } as React.CSSProperties,
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  } as React.CSSProperties,
  button: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 4,
    cursor: 'pointer',
  } as React.CSSProperties,
  cancelButton: {
    backgroundColor: colors.gray[100],
  },
  submitButton: {
    backgroundColor: colors.primary,
  },
  cancelButtonText: {
    color: colors.gray[700],
    fontSize: 14,
    fontWeight: '500',
  },
  submitButtonText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '500',
  },
  tasksList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  } as React.CSSProperties,
  taskCard: {
    backgroundColor: colors.white,
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    padding: spacing.lg,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    borderWidth: 1,
    borderColor: colors.gray[200],
    cursor: 'default',
    transition: 'all 0.2s ease',
    marginBottom: spacing.sm,
    ':hover': {
      borderColor: colors.gray[300],
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    },
    userSelect: 'none',
  } as React.CSSProperties,
  taskContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  } as React.CSSProperties,
  taskTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.dark,
    marginBottom: spacing.xs,
  },
  taskDescription: {
    fontSize: 14,
    color: colors.gray[700],
    marginBottom: spacing.xs,
  },
  dueDate: {
    fontSize: 12,
    color: colors.gray[500],
  },
  taskActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  actionButton: {
    backgroundColor: colors.white,
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.gray[200],
    cursor: 'pointer',
  } as React.CSSProperties,
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray[700],
  },
  deleteButton: {
    backgroundColor: colors.danger + '10',
  },
  deleteButtonText: {
    color: colors.danger,
  },
  headerLeft: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.md,
  } as React.CSSProperties,
  headerRight: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'center',
  } as React.CSSProperties,
  modalLabel: {
    fontSize: 12,
    marginBottom: 10,
    color: colors.dark,
    fontFamily: typography.fontFamily.body,
  },
  filterButton: {
    backgroundColor: colors.gray[100],
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: 4,
    cursor: 'pointer',
  } as React.CSSProperties,
  filterButtonText: {
    fontSize: 14,
    color: colors.gray[700],
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: colors.gray[300],
    backgroundColor: colors.white,
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,
  checkmark: {
    width: 12,
    height: 12,
    borderRadius: 2,
    backgroundColor: colors.primary,
  },
  completedTask: {
    backgroundColor: '#FFFFFF',  // Very subtle gray, just above white
    borderColor: colors.gray[600],
    borderWidth: 1.5,
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
  completedText: {
    color: colors.gray[500],
    textDecorationLine: 'line-through',
    textDecorationColor: colors.gray[400],
  },
  dragHandle: {
    cursor: 'grab',
    padding: spacing.xs,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 24,
    width: 24,
    flexShrink: 0,
    ':hover': {
      backgroundColor: colors.gray[100],
    },
    ':active': {
      cursor: 'grabbing',
      backgroundColor: colors.gray[200],
    },
    touchAction: 'none',
  } as React.CSSProperties,
  draggingTask: {
    opacity: 0.8,
    backgroundColor: '#FFFFFF',
    transform: 'scale(1.02)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    zIndex: 1,
  } as React.CSSProperties,
  completedBadge: {
    backgroundColor: 'rgba(67, 160, 71, 0.1)',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: 'rgba(67, 160, 71, 0.2)',
  } as React.CSSProperties,
  completedBadgeText: {
    color: colors.success,
    fontSize: 14,
    fontWeight: '500',
  },
} as const; 